import { useCallback, useSyncExternalStore } from 'react'

/**
 * Favourite forms — **on this device only**.
 *
 * There is no favourite, bookmark or starred column anywhere in the gateway
 * and no endpoint to set one, so this is `localStorage`, exactly as the
 * Flutter app keeps it in `GetStorage`. That is a real limitation on a
 * product whose point is the phone: a form starred on the desktop is not
 * starred on the phone. The UI therefore says so next to the Favoriten tab
 * rather than letting the star look like account state.
 *
 * Every access is wrapped: a private window, blocked site data or a full
 * quota must not take the overview down with it. A failed write means the
 * star holds for this session and is gone on reload — which is better than
 * an exception on a click.
 */

const KEY = 'formalogix.favorites'

/**
 * `useSyncExternalStore` compares snapshots by identity, so the snapshot has
 * to be a stable object that only changes when the set does — a fresh `Set`
 * per read would re-render forever.
 */
let cache: ReadonlySet<string> | null = null
const listeners = new Set<() => void>()

function parse(raw: string | null): ReadonlySet<string> {
  if (!raw) return new Set()
  try {
    const value: unknown = JSON.parse(raw)
    if (!Array.isArray(value)) return new Set()
    return new Set(value.filter((id): id is string => typeof id === 'string'))
  } catch {
    return new Set()
  }
}

/** The starred ids. Reads through a cache, so it is cheap to call in render. */
export function readFavorites(): ReadonlySet<string> {
  if (cache) return cache
  try {
    cache = parse(window.localStorage.getItem(KEY))
  } catch {
    cache = new Set()
  }
  return cache
}

function write(next: ReadonlySet<string>) {
  cache = next
  try {
    window.localStorage.setItem(KEY, JSON.stringify([...next]))
  } catch {
    // Best-effort: the set still holds for this session.
  }
  for (const listener of listeners) listener()
}

export function isFavorite(id: string): boolean {
  return readFavorites().has(id)
}

/** Adds or removes one id. Returns whether it is starred afterwards. */
export function toggleFavorite(id: string): boolean {
  const next = new Set(readFavorites())
  const starred = !next.delete(id)
  if (starred) next.add(id)
  write(next)
  return starred
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  // Another tab of the same app writing the key: `storage` fires only in the
  // *other* documents, which is exactly the case the in-process listeners
  // above do not cover.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== KEY) return
    cache = null
    listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

/** The starred ids, kept in step across every component that shows a star. */
export function useFavorites(): {
  favorites: ReadonlySet<string>
  toggle: (id: string) => void
} {
  const favorites = useSyncExternalStore(subscribe, readFavorites, () => EMPTY)
  const toggle = useCallback((id: string) => {
    toggleFavorite(id)
  }, [])
  return { favorites, toggle }
}

/** Server rendering has no `localStorage`; nothing is starred there. */
const EMPTY: ReadonlySet<string> = new Set()

/** Test seam: drops the cached snapshot. */
export function resetFavoritesCache() {
  cache = null
}
